import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDashboardActivity, DashboardActivity } from '@/hooks/useDashboardActivity';
import { formatDistanceToNow } from 'date-fns';
import { 
  Activity, 
  CheckSquare, 
  CheckCircle, 
  UserPlus, 
  FolderPlus, 
  Target,
  TrendingUp,
  MessageSquare,
  DollarSign,
  Clock,
  Trophy
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

const iconMap = {
  'activity': Activity,
  'task-created': CheckSquare,
  'task-completed': CheckCircle,
  'lead-created': UserPlus,
  'project-created': FolderPlus,
  'contact-created': UserPlus,
  'plan-created': Target,
  'status-changed': TrendingUp,
  'commented': MessageSquare,
  'budget-changed': DollarSign,
  'opportunity-created': TrendingUp,
  'opportunity-stage-changed': TrendingUp,
  'opportunity-won': Trophy,
};

const colorMap = {
  'primary': 'bg-primary',
  'success': 'bg-success',
  'warning': 'bg-warning',
  'info': 'bg-info',
  'accent': 'bg-accent',
  'secondary': 'bg-secondary',
  'destructive': 'bg-destructive',
};

const getActivityLink = (activity: DashboardActivity): string => {
  switch (activity.entityType) {
    case 'task':
      return '/tasks';
    case 'lead':
      return '/leads';
    case 'project':
      return '/projects';
    case 'contact':
      return '/contacts';
    case 'account_plan':
      return '/account-planning';
    case 'opportunity':
      return '/opportunities';
    default:
      return '#';
  }
};

const getActivityDescription = (activity: DashboardActivity): string => {
  if (activity.entityName) {
    return `${activity.title}: ${activity.entityName}`;
  }
  return activity.title;
};

export function RecentActivity() {
  const { activities, isLoading } = useDashboardActivity();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Your latest updates</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center space-x-4">
                <Skeleton className="w-2 h-2 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (activities.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Your latest updates</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Clock className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm font-medium text-foreground">No recent activity</p>
            <p className="text-sm text-muted-foreground mt-1">
              Activity from the last 30 days will appear here
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
        <CardDescription>Your latest updates</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
          {activities.map((activity) => {
            const Icon = iconMap[activity.icon] || Activity;
            const link = getActivityLink(activity);
            const isClickable = link !== '#';

            const content = (
              <>
                <div className={cn(
                  'w-2 h-2 rounded-full flex-shrink-0',
                  colorMap[activity.color]
                )} />
                <div className="flex-1 space-y-1 min-w-0">
                  <p className="text-sm font-medium leading-none">
                    {getActivityDescription(activity)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                  </p>
                </div>
              </>
            );

            if (isClickable) {
              return (
                <Link
                  key={activity.id}
                  to={link}
                  className="flex items-center space-x-4 group hover:bg-accent/50 p-2 -mx-2 rounded-lg transition-colors"
                >
                  {content}
                </Link>
              );
            }

            return (
              <div key={activity.id} className="flex items-center space-x-4">
                {content}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
